import React, { memo, useCallback, useMemo } from 'react';

import { SALESORDERS } from 'constants/Entities';
import Context from 'managers/Context';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const PLACEHOLDER_VALUE = '-';

const SalesOrdersProductLinesTotalsFooter = memo(({ data, showTaxDetails, variant = 'crud' }) => {
    const idEnvironment = useMemo(() => data?.idBranch?.value ?? data?.idBranch, [data?.idBranch]);
    const idCurrency = useMemo(
        () => data?.idCurrency?.value ?? data?.idCurrency,
        [data?.idCurrency],
    );

    const formatAmount = useCallback(
        (value, fieldId) => {
            return value != null
                ? formatDecimalByConfig({
                      value,
                      type: DECIMAL_TYPES.CURRENCY_GENERAL,
                      idEnvironment,
                      idCurrency,
                      entityName: SALESORDERS.extraFieldName,
                      fieldId,
                  })
                : PLACEHOLDER_VALUE;
        },
        [idCurrency, idEnvironment],
    );

    const legacyRecalculatedTotals = useMemo(() => {
        if (showTaxDetails || !data?.products?.length) return null;
        const manager = Context.entityManager.getEntitiesManager(SALESORDERS);
        if (!manager) return null;
        return manager.calculateEntityProducts(
            {
                products: data.products,
                discount1: data.discount1,
                discount2: data.discount2,
                discount3: data.discount3,
                discount4: data.discount4,
                currencySymbol: data.currencySymbol,
                idBranch: data.idBranch,
                idCurrency: data.idCurrency,
                shippingCostRaw: data.shippingCostRaw ?? data.shippingCost ?? null,
            },
            'edit',
        );
    }, [
        data?.currencySymbol,
        data?.discount1,
        data?.discount2,
        data?.discount3,
        data?.discount4,
        data?.idBranch,
        data?.idCurrency,
        data?.products,
        data?.shippingCost,
        data?.shippingCostRaw,
        showTaxDetails,
    ]);

    const subtotalValue = showTaxDetails
        ? data?.subtotalAmount
        : (legacyRecalculatedTotals?.subtotalAmount ??
          data?.subtotalAmount ??
          data?.subtotalRaw ??
          data?.subtotal);
    const totalFieldId = showTaxDetails ? 'finalTotalAmount' : 'totalAmount';
    const totalValue = showTaxDetails
        ? data?.finalTotalAmount
        : (data?.totalRaw ?? data?.amountSalesOrder ?? data?.total);

    const rootClassName =
        variant === 'detail'
            ? 'sales-order-product-lines-totals-footer sales-order-product-lines-totals-footer--detail'
            : 'sales-order-product-lines-totals-footer';

    return (
        <div className={rootClassName} data-testid="sales-order-product-lines-totals-footer">
            <div className="sales-order-product-lines-totals-footer__row">
                <div className="sales-order-product-lines-totals-footer__label">
                    {getLiteral('label_subtotal')}
                </div>
                <div className="sales-order-product-lines-totals-footer__value">
                    {formatAmount(subtotalValue, 'subtotalAmount')}
                </div>
            </div>
            <div className="sales-order-product-lines-totals-footer__row sales-order-product-lines-totals-footer__row--total">
                <div className="sales-order-product-lines-totals-footer__label">
                    {getLiteral('label_total')}
                </div>
                <div className="sales-order-product-lines-totals-footer__value">
                    {formatAmount(totalValue, totalFieldId)}
                </div>
            </div>
        </div>
    );
});

SalesOrdersProductLinesTotalsFooter.displayName = 'SalesOrdersProductLinesTotalsFooter';

export default SalesOrdersProductLinesTotalsFooter;
